import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet-draw';
import html2canvas from 'html2canvas';

const MapDrawingTool = ({ onMapImageCapture }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const drawnItemsRef = useRef(null);
  const [hasDrawing, setHasDrawing] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [captureStatus, setCaptureStatus] = useState(null);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Initialize map centered on UK
    const map = L.map(mapRef.current, {
      preferCanvas: true
    }).setView([52.4862, -1.8904], 13);
    mapInstanceRef.current = map;

    // Add OpenStreetMap tiles
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19
    }).addTo(map);

    // Initialize the FeatureGroup to store editable layers
    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);
    drawnItemsRef.current = drawnItems;

    // Initialize the draw control with Green Frog Utilities brand colors
    const drawControl = new L.Control.Draw({
      position: 'topright',
      draw: {
        polygon: {
          allowIntersection: false,
          shapeOptions: {
            color: '#059669',      // Emerald green
            fillColor: '#10b981', // Lighter emerald
            fillOpacity: 0.3,
            weight: 3
          }
        },
        rectangle: {
          shapeOptions: {
            color: '#059669',
            fillColor: '#10b981',
            fillOpacity: 0.3,
            weight: 3
          }
        },
        circle: false,
        circlemarker: false,
        marker: true,
        polyline: false
      },
      edit: {
        featureGroup: drawnItems,
        remove: true
      }
    });
    map.addControl(drawControl);

    // Handle drawing created
    map.on(L.Draw.Event.CREATED, (e) => {
      const layer = e.layer;
      drawnItems.addLayer(layer);
      setHasDrawing(true);
      setCaptureStatus(null);
    });

    // Handle drawing edited
    map.on(L.Draw.Event.EDITED, () => {
      setCaptureStatus(null);
    });

    // Handle drawing deleted
    map.on(L.Draw.Event.DELETED, () => {
      if (drawnItems.getLayers().length === 0) {
        setHasDrawing(false);
        onMapImageCapture(null);
        setCaptureStatus(null);
      }
    });

    // Add instructions
    const info = L.control({ position: 'topleft' });
    info.onAdd = function() {
      const div = L.DomUtil.create('div', 'map-info');
      div.innerHTML = '<div style="background: white; padding: 10px; border-radius: 5px; box-shadow: 0 2px 5px rgba(0,0,0,0.2); font-size: 12px;"><strong style="color: #059669;">Instructions:</strong><br/>1. Search for your location<br/>2. Draw around your building<br/>3. Click "Capture Map" button</div>';
      return div;
    };
    info.addTo(map);

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [onMapImageCapture]);

  // Manual capture function
  const handleCaptureMap = async () => {
    if (!mapRef.current || isCapturing) return;
    
    setIsCapturing(true);
    setCaptureStatus(null);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const canvas = await html2canvas(mapRef.current, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        scrollX: 0,
        scrollY: 0,
        windowWidth: mapRef.current.offsetWidth,
        windowHeight: mapRef.current.offsetHeight
      });
      
      const imageData = canvas.toDataURL('image/png');
      onMapImageCapture(imageData);
      setCaptureStatus('success');
    } catch (error) {
      console.error('Error capturing map image:', error);
      setCaptureStatus('error');
    } finally {
      setIsCapturing(false);
    }
  };

  const handleSearch = () => {
    const searchInput = document.getElementById('map-search-input');
    const query = searchInput?.value;
    
    if (!query || !mapInstanceRef.current) return;

    fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}`)
      .then(response => response.json())
      .then(data => {
        if (data && data.length > 0) {
          const { lat, lon } = data[0];
          mapInstanceRef.current.setView([lat, lon], 18);
          setCaptureStatus(null);
        } else {
          alert('Location not found. Please try a different search term.');
        }
      })
      .catch(error => {
        console.error('Geocoding error:', error);
        alert('Error searching for location. Please try again.');
      });
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-semibold mb-2 text-gray-900">
          Building Location
        </label>
        <p className="text-sm text-gray-600 mb-2">
          Search for your site, draw a shape around it, then click "Capture Map" to include in your message.
        </p>
        
        {/* Search bar */}
        <div className="flex gap-2 mb-2">
          <input
            id="map-search-input"
            type="text"
            placeholder="Search for address or postcode..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSearch();
              }
            }}
          />
          <button
            type="button"
            onClick={handleSearch}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm font-semibold transition-colors"
          >
            Search
          </button>
        </div>
      </div>
      
      <div 
        ref={mapRef} 
        style={{ 
          height: '400px', 
          width: '100%', 
          borderRadius: '8px',
          border: '2px solid #e5e7eb',
          position: 'relative',
          zIndex: 1
        }}
      />
      
      {/* Capture button */}
      {hasDrawing && (
        <div className="space-y-2">
          <button
            type="button"
            onClick={handleCaptureMap}
            disabled={isCapturing}
            className="w-full px-4 py-3 text-white rounded-md text-sm font-semibold transition-all"
            style={{ 
              backgroundColor: captureStatus === 'success' ? '#22c55e' : '#047857',
              opacity: isCapturing ? 0.7 : 1
            }}
          >
            {isCapturing ? (
              'Capturing...'
            ) : captureStatus === 'success' ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Map Captured - Ready to Submit
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Capture Map Screenshot
              </span>
            )}
          </button>
          
          {captureStatus === 'success' && (
            <p className="text-sm text-center text-green-600 font-medium">
              The map view above will be attached to your message exactly as shown.
            </p>
          )}
          
          {captureStatus === 'error' && (
            <p className="text-sm text-center text-red-600 font-medium">
              Failed to capture map. Please try again.
            </p>
          )}
        </div>
      )}
      
      {!hasDrawing && (
        <div className="flex items-center gap-2 p-3 rounded-md bg-emerald-50 border border-emerald-200">
          <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm text-emerald-700">
            Use the drawing tools on the right side of the map to mark your site area.
          </span>
        </div>
      )}
    </div>
  );
};

export default MapDrawingTool;
